import { useState, useEffect } from "react";
import moment from "moment";
import { Input, DatePicker, Row, Col } from "antd";
import {
  MdAssignment,
  MdGroups,
  MdEvent,
  MdNoteAlt,
  MdInfo,
  MdSettings,
  MdCheckCircle,
  MdArrowBack,
} from "react-icons/md";
import { useFilters } from "core/features/filters/store";
import useStore from "../hooks/useStore";
import useGetCentroCusto from "../hooks/useGetCentroCusto";
import useGetEquipes from "../hooks/useGetEquipes";
import useGetOperacoes from "../hooks/useGetOperacoes";
import useGetSistemaAplicacao from "../hooks/useGetSistemaAplicacao";
import useGetTipoBico from "../hooks/useGetTipoBico";
import useGetFlagsDadosTecnicos from "../hooks/useGetFlagsDadosTecnicos";
import {
  StyledModal,
  Title,
  Subtitle,
  StyledSelect,
  ButtonStyled,
  ButtonStyledLink,
  ContentButton,
} from "./styles";

const { TextArea } = Input;

interface ModalCadastroOrdemServicoProps {
  isOpen: boolean;
  planoOperacionalData: any;
  safraPeriodoIdExterno?: string;
  unidadeIdExterno?: string;
  onClose: () => void;
  back: () => void;
  next: () => void;
}

function ModalCadastroOrdemServico({
  isOpen,
  planoOperacionalData,
  safraPeriodoIdExterno,
  unidadeIdExterno,
  onClose,
  back,
  next,
}: ModalCadastroOrdemServicoProps) {

  const dataAbertura = moment();

  const filtrosPlanoOperacional = planoOperacionalData.filtrosAvancados;

  const { filters } = useFilters();
  const { filtersAdvanced, unidade, safra } = filters;

  const {
    centroCusto,
    equipe,
    operacao,
    previsaoInicio,
    previsaoEncerramento,
    sistemaAplicacao,
    tipoBico,
    vazaoProgramada,
    quantidadeTanques,
    capacidadeTanque,
    volumeTanqueExtra,
    larguraFaixa,
    observacoes,
    setCentroCusto,
    setEquipe,
    setEquipeNome,
    setOperacao,
    setOperacaoNome,
    setPrevisaoInicio,
    setPrevisaoEncerramento,
    setSistemaAplicacao,
    setTipoBico,
    setVazaoProgramada,
    setQuantidadeTanques,
    setCapacidadeTanque,
    setVolumeTanqueExtra,
    setLarguraFaixa,
    setObservacoes,
    reset,
  } = useStore();

  const { data: centrosCusto, isLoading: loadingCentroCusto } = useGetCentroCusto(unidadeIdExterno, safraPeriodoIdExterno);
  const { data: equipes, isLoading: loadingEquipes } = useGetEquipes(unidade?.id);
  const { data: operacoes, isLoading: loadingOperacoes } = useGetOperacoes(centroCusto);
  const { data: sistemasAplicacoes, isLoading: loadingSistemasAplicacoes } = useGetSistemaAplicacao();
  const { data: tiposBicos, isLoading: loadingTiposBicos } = useGetTipoBico();
  const { data: flagsDadosTecnicos, isLoading: loadingFlagsDadosTecnicos } = useGetFlagsDadosTecnicos(operacao);

  const [isCentroCustoValid, setIsCentroCustoValid] = useState<boolean>();
  const [isPrevisaoInicioValid, setIsPrevisaoInicioValid] = useState<boolean>();
  const [isPrevisaoEncerramentoValid, setIsPrevisaoEncerramentoValid] = useState<boolean>();
  const [isEquipeValid, setIsEquipeValid] = useState<boolean>();
  const [isOperacaoValid, setIsOperacaoValid] = useState<boolean>();
  const [isVazaoValid, setIsVazaoValid] = useState<boolean>();
  const [isCapacidadeValid, setIsCapacidadeValid] = useState<boolean>();
  const [isLarguraValid, setIsLarguraValid] = useState<boolean>();
  const [isSistemaValid, setIsSistemaValid] = useState<boolean>();
  const [isBicoValid, setIsBicoValid] = useState<boolean>();

  const isVazaoRequired = flagsDadosTecnicos?.vazaoProgramada === 3;
  const isCapacidadeRequired = flagsDadosTecnicos?.capacidadeTanque === 3 || (flagsDadosTecnicos?.capacidadeTanque === 4 && !!vazaoProgramada);
  const isLarguraRequired = flagsDadosTecnicos?.larguraFaixa === 3;
  const isSistemaRequired = flagsDadosTecnicos?.sistemaAplicacao === 3;
  const isBicoRequired = flagsDadosTecnicos?.bico === 3;

  const isSubmitAllowed =
    !!centroCusto
    && !!equipe
    && !!operacao
    && !!previsaoInicio
    && !!previsaoEncerramento
    && ((!!vazaoProgramada && isVazaoRequired) || !isVazaoRequired)
    && ((!!capacidadeTanque && isCapacidadeRequired) || !isCapacidadeRequired)
    && ((!!larguraFaixa && isLarguraRequired) || !isLarguraRequired)
    && ((!!sistemaAplicacao && isSistemaRequired) || !isSistemaRequired)
    && ((!!tipoBico && isBicoRequired) || !isBicoRequired);

  function handleSubmit() {
    setIsCentroCustoValid(!!centroCusto);
    setIsPrevisaoInicioValid(!!previsaoInicio);
    setIsPrevisaoEncerramentoValid(!!previsaoEncerramento);
    setIsEquipeValid(!!equipe);
    setIsOperacaoValid(!!operacao);
    setIsVazaoValid(isVazaoRequired ? !!vazaoProgramada : true);
    setIsCapacidadeValid(isCapacidadeRequired ? !!capacidadeTanque : true);
    setIsLarguraValid(isLarguraRequired ? !!larguraFaixa : true);
    setIsSistemaValid(isSistemaRequired ? !!sistemaAplicacao : true);
    setIsBicoValid(isBicoRequired ? !!tipoBico : true);
    if (!isSubmitAllowed) return;
    next();
  }

  useEffect(() => {
    reset();
  }, [planoOperacionalData]);
  
  useEffect(() => {
    setCentroCusto(centrosCusto?.[0]?.value);
    
  }, [centrosCusto]);

  useEffect(() => {
    if (previsaoInicio && previsaoEncerramento) {
      if (previsaoInicio.isAfter(previsaoEncerramento)) {
        setPrevisaoEncerramento(null);
        setIsPrevisaoEncerramentoValid(false)
      } else {
        setIsPrevisaoEncerramentoValid(true)
      }
    }
  }, [previsaoInicio, previsaoEncerramento])

  return (
    <StyledModal
      centered
      width={"95%"}
      zIndex={999999}
      visible={isOpen}
      onCancel={onClose}
      title={
        <Title>
          <MdAssignment color="#fff" size={28} />
          Cadastro de Ordem de Serviço
        </Title>
      }
      footer={
          <ContentButton>
            <ButtonStyledLink
              size="large"
              onClick={back}
              icon={<MdArrowBack size={24} />}
            >Voltar</ButtonStyledLink>
            <ButtonStyled
              type="primary"
              size="large"
              onClick={handleSubmit}
              icon={<MdCheckCircle size={24} />}
            >Próximo</ButtonStyled>
          </ContentButton>
      }
    >
      <Row gutter={[8, 8]}>
        <Col span={16}>
          <Subtitle><MdInfo size={20} color="#2196f3" /> Dados Gerais da Ordem de Serviço</Subtitle>
          <Row gutter={[8, 16]}>
            <Col span={6}>
              <p style={{ marginBottom: 4 }}>Data de Abertura <sup style={{ color: "#f00" }}>*</sup></p>
              <DatePicker
                disabled
                size="large"
                placeholder="Data de Abertura"
                format="DD/MM/YYYY"
                value={dataAbertura}
                style={{ width: "100%", borderRadius: 10 }}
              />
            </Col>
            <Col span={6}>
              <p style={{ marginBottom: 4 }}>Ano Agrícola <sup style={{ color: "#f00" }}>*</sup></p>
              <Input
                disabled
                size="large"
                placeholder="Ano Agrícola"
                value={safra?.descricao}
                style={{ borderRadius: 10 }}
              />
            </Col>
            <Col span={6}>
              <p style={{ marginBottom: 4 }}>Período de Produção <sup style={{ color: "#f00" }}>*</sup></p>
              <StyledSelect
                disabled
                size="large"
                placeholder="Período de Produção"
                value={filtersAdvanced.safraPeriodo}
                options={filtrosPlanoOperacional.safraPeriodos}
                fieldNames={{ label: "nome", value: "safraPeriodoId" }}
              />
            </Col>
            <Col span={6}>
              <p style={{ marginBottom: 4 }}>Centro de Custo <sup style={{ color: "#f00" }}>*</sup></p>
              <StyledSelect
                allowClear
                showSearch
                optionFilterProp="text"
                size="large"
                placeholder="Centro de Custo"
                loading={loadingCentroCusto}
                options={centrosCusto}
                value={centroCusto}
                // value={centrosCusto?.[0]?.value}
                onChange={e => setCentroCusto(e as string)}
                disabled={loadingCentroCusto}
                fieldNames={{ label: "text", value: "value" }}
                borderColor={isCentroCustoValid === false ? "#f00" : "#d9d9d9"}
              />
            </Col>
          </Row>
        </Col>
        <Col span={8}>
          <Subtitle><MdEvent size={20} color="#ff9800" /> Previsão de Execução</Subtitle>
          <Row gutter={[8, 16]}>
            <Col span={12}>
              <p style={{ marginBottom: 4 }}>Previsão de Início <sup style={{ color: "#f00" }}>*</sup></p>
              <DatePicker
                allowClear
                size="large"
                placeholder="Previsão de Início"
                format="DD/MM/YYYY"
                value={previsaoInicio}
                onChange={setPrevisaoInicio}
                style={{
                  width: "100%",
                  borderRadius: 10,
                  borderColor: isPrevisaoInicioValid === false ? "#f00" : "#d9d9d9",
                }}
              />
            </Col>
            <Col span={12}>
              <p style={{ marginBottom: 4 }}>Previsão de Encerramento <sup style={{ color: "#f00" }}>*</sup></p>
              <DatePicker
                allowClear
                size="large"
                placeholder="Previsão de Encerramento"
                format="DD/MM/YYYY"
                value={previsaoEncerramento}
                onChange={setPrevisaoEncerramento}
                style={{
                  width: "100%",
                  borderRadius: 10,
                  borderColor: isPrevisaoEncerramentoValid === false ? "#f00" : "#d9d9d9",
                }}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Subtitle><MdGroups size={20} color="#4caf50" /> Equipe e Operação</Subtitle>
      <Row gutter={[8, 16]}>
        <Col span={12}>
          <p style={{ marginBottom: 4 }}>Equipe <sup style={{ color: "#f00" }}>*</sup></p>
          <StyledSelect
            allowClear
            showSearch
            optionFilterProp="text"
            size="large"
            placeholder="Equipe"
            loading={loadingEquipes}
            options={equipes}
            value={equipe}
            onChange={(value, option: any) => {
              setEquipe(value as string);
              setEquipeNome(option?.text);
            }}
            disabled={loadingEquipes}
            fieldNames={{ label: "text", value: "value" }}
            borderColor={isEquipeValid === false ? "#f00" : "#d9d9d9"}
          />
        </Col>
        <Col span={12}>
          <p style={{ marginBottom: 4 }}>Operação da OS <sup style={{ color: "#f00" }}>*</sup></p>
          <StyledSelect
            allowClear
            showSearch
            optionFilterProp="text"
            size="large"
            placeholder="Operação da OS"
            loading={loadingOperacoes}
            options={operacoes}
            value={operacao}
            onChange={(value, option: any) => {
              setOperacao(value as string);
              setOperacaoNome(option?.text);
            }}
            disabled={!centroCusto || loadingOperacoes}
            fieldNames={{ label: "text", value: "value" }}
            borderColor={isOperacaoValid === false ? "#f00" : "#d9d9d9"}
          />
        </Col>
      </Row>

      {operacao && !loadingFlagsDadosTecnicos && (
        <>
          <Subtitle><MdSettings size={20} color="#9c27b0" /> Dados Técnicos</Subtitle>
          <Row gutter={[8, 16]}>
            <Col span={4}>
              <p style={{ marginBottom: 4 }}>Vazão Programada (L/ha) {isVazaoRequired && <sup style={{ color: "#f00" }}>*</sup>}</p>
              <Input
                allowClear
                type="number"
                size="large"
                placeholder="Ex: 150.5"
                value={vazaoProgramada}
                onChange={e => setVazaoProgramada(e.target.value)}
                disabled={flagsDadosTecnicos?.vazaoProgramada === 1}
                style={{
                  borderRadius: 10,
                  borderColor: isVazaoValid === false ? "#f00" : "#d9d9d9",
                }}
              />
            </Col>
            <Col span={3}>
              <p style={{ marginBottom: 4 }}>Quantidade de Tanques</p>
              <Input
                allowClear
                type="number"
                size="large"
                placeholder="Ex: 2"
                value={quantidadeTanques}
                onChange={e => setQuantidadeTanques(e.target.value)}
                disabled={
                  flagsDadosTecnicos?.quantidadeTanque === 1
                  || (flagsDadosTecnicos?.quantidadeTanque === 2 && flagsDadosTecnicos?.vazaoProgramada === 1 && flagsDadosTecnicos?.capacidadeTanque === 1)
                }
                style={{ borderRadius: 10 }}
              />
            </Col>
            <Col span={3}>
              <p style={{ marginBottom: 4 }}>Capacidade do Tanque (L) {isCapacidadeRequired && <sup style={{ color: "#f00" }}>*</sup>}</p>
              <Input
                allowClear
                type="number"
                size="large"
                placeholder="Ex: 3000.0"
                value={capacidadeTanque}
                onChange={e => setCapacidadeTanque(e.target.value)}
                disabled={flagsDadosTecnicos?.capacidadeTanque === 1}
                style={{
                  borderRadius: 10,
                  borderColor: isCapacidadeValid === false ? "#f00" : "#d9d9d9",
                }}
              />
            </Col>
            <Col span={3}>
              <p style={{ marginBottom: 4 }}>Volume do Tanque Extra (L)</p>
              <Input
                allowClear
                type="number"
                size="large"
                placeholder="Ex: 500.0"
                value={volumeTanqueExtra}
                onChange={e => setVolumeTanqueExtra(e.target.value)}
                disabled={
                  flagsDadosTecnicos?.volumeTanqueExtra === 1
                  || (flagsDadosTecnicos?.quantidadeTanque === 2 && flagsDadosTecnicos?.vazaoProgramada === 1 && flagsDadosTecnicos?.capacidadeTanque === 1)
                }
                style={{ borderRadius: 10 }}
              />
            </Col>
            <Col span={3}>
              <p style={{ marginBottom: 4 }}>Largura da Faixa (m) {isLarguraRequired && <sup style={{ color: "#f00" }}>*</sup>}</p>
              <Input
                allowClear
                type="number"
                size="large"
                placeholder="Ex: 18.0"
                value={larguraFaixa}
                onChange={e => setLarguraFaixa(e.target.value)}
                disabled={flagsDadosTecnicos?.larguraFaixa === 1}
                style={{
                  borderRadius: 10,
                  borderColor: isLarguraValid === false ? "#f00" : "#d9d9d9",
                }}
              />
            </Col>
            <Col span={4}>
              <p style={{ marginBottom: 4 }}>Sistema de Aplicação {isSistemaRequired && <sup style={{ color: "#f00" }}>*</sup>}</p>
              <StyledSelect
                allowClear
                showSearch
                optionFilterProp="text"
                size="large"
                placeholder="Sistema de Aplicação"
                loading={loadingSistemasAplicacoes}
                options={sistemasAplicacoes}
                value={sistemaAplicacao}
                onChange={e => setSistemaAplicacao(e as string)}
                disabled={loadingSistemasAplicacoes || flagsDadosTecnicos?.sistemaAplicacao === 1}
                fieldNames={{ label: "text", value: "value" }}
                borderColor={isSistemaValid === false ? "#f00" : "#d9d9d9"}
              />
            </Col>
            <Col span={4}>
              <p style={{ marginBottom: 4 }}>Tipo de Bico {isBicoRequired && <sup style={{ color: "#f00" }}>*</sup>}</p>
              <StyledSelect
                allowClear
                showSearch
                optionFilterProp="text"
                size="large"
                placeholder="Tipo de Bico"
                loading={loadingTiposBicos}
                options={tiposBicos}
                value={tipoBico}
                onChange={e => setTipoBico(e as string)}
                disabled={loadingTiposBicos || flagsDadosTecnicos?.bico === 1}
                fieldNames={{ label: "text", value: "value" }}
                borderColor={isBicoValid === false ? "#f00" : "#d9d9d9"}
              />
            </Col>
          </Row>
        </>
      )}

      <Subtitle><MdNoteAlt size={20} color="#ff9800" /> Observações (Opcional)</Subtitle>
      <TextArea
        size="large"
        placeholder="Adicione observações relevantes sobre esta Ordem de Serviço..."
        rows={3}
        value={observacoes}
        onChange={e => setObservacoes(e.target.value)}
        style={{ borderRadius: 10 }}
      />
    </StyledModal>
  );
}

export default ModalCadastroOrdemServico;
